/**
 * Microsoft Advertising (Bing Ads) OAuth Utilities (Story 19.1, Task 2)
 *
 * Handles OAuth 2.0 + PKCE URL building, code exchange, refresh, and error parsing
 * against the Microsoft identity platform v2 endpoint
 * (login.microsoftonline.com/common/oauth2/v2.0).
 *
 * Key characteristics:
 * - PKCE required (code_challenge_method=S256). Microsoft "strongly recommends"
 *   PKCE for confidential clients; we apply it for defense-in-depth and to keep
 *   our OAuth flows uniform with Klaviyo.
 * - Scope set: `https://ads.microsoft.com/msads.manage offline_access` — the
 *   second scope makes Microsoft return a refresh_token.
 * - prompt=select_account so users with multiple Microsoft accounts can pick.
 * - Refresh tokens last ~90 days but Microsoft may rotate them on every refresh
 *   — callers MUST persist the new refresh_token if one is returned. ADR-020.
 * - Token exchange + refresh use application/x-www-form-urlencoded body.
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

const MICROSOFT_AUTHORIZE_URL =
  "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";
const MICROSOFT_TOKEN_URL =
  "https://login.microsoftonline.com/common/oauth2/v2.0/token";
const TOKEN_EXCHANGE_TIMEOUT_MS = 25_000;

/**
 * Scopes requested for Microsoft Advertising integration.
 * `msads.manage` grants the access token recognised by `clientcenter.api.bingads.microsoft.com`.
 * `offline_access` is required so Microsoft returns a refresh_token.
 */
export const MICROSOFT_ADS_SCOPES = [
  "https://ads.microsoft.com/msads.manage",
  "offline_access",
] as const;

/**
 * Microsoft public-client refresh tokens last 90 days from the most recent
 * successful refresh. Calls to `refreshMicrosoftAdsToken` may rotate the
 * refresh token and reset this lifetime — see ADR-020.
 */
export const MICROSOFT_ADS_REFRESH_TOKEN_LIFETIME_MS =
  90 * 24 * 60 * 60 * 1000;

// ============================================================================
// Types
// ============================================================================

export interface MicrosoftAdsAuthUrlOptions {
  clientId: string;
  redirectUri: string;
  /** KV state key to embed in state param */
  stateKey: string;
  /** PKCE code_challenge (Base64URL of SHA-256 of code_verifier) */
  codeChallenge: string;
  /**
   * Microsoft `prompt` parameter. Defaults to `select_account` for the typical
   * onboarding flow. Pass `consent` to force the consent screen (e.g., when
   * msads.manage scopes are expanded in a future Microsoft API version and
   * users need to re-grant permissions). (review #3 M4)
   */
  prompt?: "select_account" | "consent" | "login" | "none";
}

export interface MicrosoftAdsTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

export interface MicrosoftAdsCallbackError {
  code: string;
  userMessage: string;
  userAction: "retry" | "none";
}

// ============================================================================
// Build Authorization URL
// ============================================================================

/**
 * Builds the Microsoft Advertising OAuth 2.0 + PKCE authorization URL.
 *
 * Mirrors `buildKlaviyoAuthUrl()` and adds Microsoft-specific params:
 *   - `prompt=select_account` so users on multiple MS accounts can choose
 *   - `scope` is space-joined per OAuth 2.0 spec
 */
export function buildMicrosoftAdsAuthUrl(
  options: MicrosoftAdsAuthUrlOptions
): string {
  const { clientId, redirectUri, stateKey, codeChallenge, prompt } = options;

  if (!clientId) {
    throw new Error("MICROSOFT_ADS_CLIENT_ID is required");
  }
  if (!redirectUri) {
    throw new Error("MICROSOFT_ADS_OAUTH_REDIRECT_URI is required");
  }
  if (!stateKey) {
    throw new Error("stateKey is required for CSRF protection");
  }
  if (!codeChallenge) {
    throw new Error("codeChallenge is required for PKCE");
  }

  const url = new URL(MICROSOFT_AUTHORIZE_URL);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", stateKey);
  url.searchParams.set("scope", MICROSOFT_ADS_SCOPES.join(" "));
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", codeChallenge);
  url.searchParams.set("prompt", prompt ?? "select_account");

  return url.toString();
}

// ============================================================================
// Token Exchange
// ============================================================================

/**
 * Exchanges an authorization code for access + refresh tokens with PKCE.
 *
 * CRITICAL: Microsoft requires `application/x-www-form-urlencoded` for the
 * token endpoint. JSON bodies will be rejected with `invalid_request`.
 */
export async function exchangeMicrosoftAdsCode(
  code: string,
  codeVerifier: string,
  clientId: string,
  clientSecret: string,
  redirectUri: string
): Promise<MicrosoftAdsTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    client_secret: clientSecret,
    redirect_uri: redirectUri,
    code,
    code_verifier: codeVerifier,
  });

  const result = await safeFetch(
    MICROSOFT_TOKEN_URL,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    // safeFetch already sanitizes provider error bodies via providerMessage —
    // include it in the thrown error so refresh-callers can detect cases like
    // `invalid_grant` (refresh-token revoked) and surface a re-connect prompt.
    const detail = result.error.providerMessage
      ? extractOAuthErrorDescription(result.error.providerMessage)
      : "";
    throw new Error(
      sanitizeErrorMessage(
        `Microsoft Ads token exchange failed: ${detail || result.error.message}`
      )
    );
  }

  const response = result.data;
  // safeFetch only returns success=true when response.ok, so we can read JSON directly.
  const data = (await response.json()) as MicrosoftAdsTokenResponse;

  validateTokenResponse(data, /* requireRefreshToken */ true);

  return data;
}

// ============================================================================
// Token Refresh
// ============================================================================

/**
 * Refreshes a Microsoft Ads access token via the refresh_token grant.
 *
 * Microsoft *may* return a new refresh_token on every refresh ("refresh token
 * rotation"). Callers MUST persist the rotated refresh_token if one is returned
 * — otherwise the next refresh will fail with `invalid_grant`. See ADR-020.
 *
 * `scope` is required on refresh requests for the v2 endpoint, otherwise
 * Microsoft returns `invalid_scope`.
 */
export async function refreshMicrosoftAdsToken(
  refreshToken: string,
  clientId: string,
  clientSecret: string
): Promise<MicrosoftAdsTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: clientId,
    client_secret: clientSecret,
    refresh_token: refreshToken,
    scope: MICROSOFT_ADS_SCOPES.join(" "),
  });

  const result = await safeFetch(
    MICROSOFT_TOKEN_URL,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    const detail = result.error.providerMessage
      ? extractOAuthErrorDescription(result.error.providerMessage)
      : "";
    throw new Error(
      sanitizeErrorMessage(
        `Microsoft Ads token refresh failed: ${detail || result.error.message}`
      )
    );
  }

  const response = result.data;
  // safeFetch only returns success=true when response.ok, so we can read JSON directly.
  const data = (await response.json()) as MicrosoftAdsTokenResponse;

  // Refresh responses MAY omit refresh_token (when Microsoft chooses not to
  // rotate). The caller (microsoftAdsAdapter.refreshToken) handles the missing
  // value by reusing the current refresh_token.
  validateTokenResponse(data, /* requireRefreshToken */ false);

  return data;
}

// ============================================================================
// Internal: token-response validation (review #3 H2)
// ============================================================================

/**
 * Validates that a Microsoft v2 token response has the fields we require and
 * that they're the correct type. Microsoft type-tested values like `expires_in`
 * are documented as integers, but a future schema drift (e.g., `"3600"` string)
 * would silently break `Date.now() + expires_in * 1000` — string-multiplication
 * yields NaN. We catch that here and throw a clear error instead.
 */
function validateTokenResponse(
  data: MicrosoftAdsTokenResponse,
  requireRefreshToken: boolean
): void {
  if (typeof data.access_token !== "string" || data.access_token.length === 0) {
    throw new Error("Invalid Microsoft Ads token response: missing access_token");
  }
  if (requireRefreshToken) {
    if (
      typeof data.refresh_token !== "string" ||
      data.refresh_token.length === 0
    ) {
      throw new Error(
        "Invalid Microsoft Ads token response: missing refresh_token"
      );
    }
  }
  if (typeof data.expires_in !== "number" || !Number.isFinite(data.expires_in)) {
    throw new Error(
      `Invalid Microsoft Ads token response: expires_in must be a finite number (received ${typeof data.expires_in})`
    );
  }
}

// ============================================================================
// Callback Error Parsing
// ============================================================================

/**
 * Parses Microsoft identity platform OAuth callback errors from query params.
 *
 * Microsoft returns standard OAuth 2.0 error codes as `?error=...&error_description=...`.
 * We map them to user-facing messages and a retry hint.
 *
 * @see https://learn.microsoft.com/en-us/entra/identity-platform/reference-error-codes
 */
export function parseMicrosoftAdsCallbackError(
  searchParams: URLSearchParams
): MicrosoftAdsCallbackError | null {
  const error = searchParams.get("error");
  if (!error) return null;

  if (error === "access_denied") {
    return {
      code: "oauth-denied",
      userMessage:
        "You denied the Microsoft Advertising connection request.",
      userAction: "retry",
    };
  }

  if (
    error === "invalid_request" ||
    error === "unauthorized_client" ||
    error === "redirect_uri_mismatch"
  ) {
    return {
      code: "oauth-config-error",
      userMessage:
        "Microsoft Advertising connection is misconfigured. Contact support.",
      userAction: "none",
    };
  }

  if (error === "server_error" || error === "temporarily_unavailable") {
    return {
      code: "oauth-error",
      userMessage:
        "Microsoft Advertising is temporarily unavailable. Please try again later.",
      userAction: "retry",
    };
  }

  if (error === "invalid_scope") {
    return {
      code: "oauth-config-error",
      userMessage:
        "Microsoft Advertising connection is misconfigured (invalid scopes). Contact support.",
      userAction: "none",
    };
  }

  return {
    code: "oauth-error",
    userMessage:
      "An error occurred connecting to Microsoft Advertising. Please try again.",
    userAction: "retry",
  };
}

// ============================================================================
// Internal: extract the OAuth `error`/`error_description` from a sanitized body
// ============================================================================
//
// Microsoft v2 token errors come back as a JSON envelope:
//   { "error": "invalid_grant", "error_description": "AADSTS70008: ..." }
// `safeFetch` exposes the (sanitized) response body via `providerMessage`. We
// pluck out the `error` and `error_description` so adapter callers can detect
// `invalid_grant` and prompt re-consent without having to re-parse the body.

function extractOAuthErrorDescription(providerBody: string): string {
  try {
    const parsed = JSON.parse(providerBody) as {
      error?: unknown;
      error_description?: unknown;
    };
    const err =
      typeof parsed.error === "string" && parsed.error.length > 0
        ? parsed.error
        : "";
    const desc =
      typeof parsed.error_description === "string" &&
      parsed.error_description.length > 0
        ? parsed.error_description
        : "";

    if (err && desc) return `${err}: ${desc}`;
    if (desc) return desc;
    if (err) return err;
    // Empty envelope `{}` or non-string fields → fall back to caller's
    // synthetic message.
    return "";
  } catch {
    // Not JSON — return empty string so the caller falls back to safeFetch's
    // synthetic message ("HTTP 400 from login.microsoftonline.com: ..."). The
    // alternative (returning the raw, possibly truncated/sanitized body) can
    // surface partial JSON or HTML to user-facing errors. Microsoft v2
    // consistently returns JSON on token-endpoint failures, so this branch is
    // a defensive fallback only.
    return "";
  }
}
